/**
 * The RTL sizer's Review-mode content: the KPI strip an underwriter reads first
 * (required coupon, total loan, initial advance, rehab holdback, borrower class,
 * liquidity) and the result panels three across.
 *
 * Render-only, like RtlResultsColumn: the rate, class and liquidity are response
 * fields, and the three amounts are what the backend's normalizer wrote onto the
 * STORED scenario for this version (`baseLoanAmount` / `totalHoldback` /
 * `totalLoanAmount`) — nothing is recomputed here.
 */
import { fmtMoney, fmtPct, fmtRate } from "../sizerFormat";
import {
  rtlBuyRate,
  rtlRequiredCoupon,
} from "../../sizersShared/rtlRateDisplay";
import {
  KpiCell,
  KpiHero,
  KpiStrip,
} from "../../sizersShared/workspace/ScenarioReview";
import RtlResultsColumn, { rtlRateChips } from "./RtlResultsColumn";
import { ExplainCard } from "../../sizersShared/workspace/ExplainResultPanel";
import Flx from "../../../components/layout/Flx";

const pctOf = (v) => {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isNaN(n) ? null : n > 1 ? n / 100 : n;
};

export function RtlKpis({ result, scenario, version }) {
  const rate = result?.rate || {};
  const req = result?.requirements || {};
  const s = scenario || {};
  const resolved = result?.resolved || {};
  const cl = resolved.classification || {};
  const cls = resolved.borrowerClass || cl.borrowerClass;
  const score =
    cl.totalScore ??
    (cl.creditScore != null || cl.experienceScore != null
      ? (Number(cl.creditScore) || 0) + (Number(cl.experienceScore) || 0)
      : null);
  // §12b: the response itemises the total — prefer it over the stored scenario.
  const z = result?.sizing || {};
  const totalLoan = z.totalLoanAmount ?? s.totalLoanAmount;
  const baseLoan = z.baseLoanAmount ?? s.baseLoanAmount;
  const financedCc = Number(z.financedClosingCosts) || 0;
  const financedReserve = Number(z.capitalizedInterestReserve) || 0;
  const lev = pctOf(s.sizerInitialRequestedLeverage ?? z.sizerInitialRequestedLeverage);
  const financed = pctOf(s.financedBudgetPercent);
  const buy = rtlBuyRate(rate);

  return (
    <KpiStrip
      hero={
        <KpiHero
          label={`Required coupon${version ? ` · v${version}` : ""}`}
          value={rate.error ? "—" : fmtRate(rtlRequiredCoupon(rate))}
          sub={
            rate.error
              ? rate.error
              : buy != null
                ? `Buy rate ${fmtRate(buy)}`
                : null
          }
          chips={rate.error ? [] : rtlRateChips(rate)}
        />
      }
    >
      <KpiCell
        label="Total loan"
        value={fmtMoney(totalLoan)}
        caption={[
          s.termMonths ? `${s.termMonths} mo` : null,
          s.loanPurpose,
        ]
          .filter(Boolean)
          .join(" · ")}
      />
      <KpiCell
        label="Initial advance"
        value={fmtMoney(baseLoan)}
        caption={
          [
            lev != null ? `${fmtPct(lev, 0)} requested leverage` : null,
            financedReserve ? `incl. ${fmtMoney(financedReserve)} reserve` : null,
            financedCc ? `incl. ${fmtMoney(financedCc)} closing costs` : null,
          ]
            .filter(Boolean)
            .join(" · ") || null
        }
      />
      <KpiCell
        label="Rehab holdback"
        value={fmtMoney(z.constructionHoldback ?? s.totalHoldback)}
        caption={financed != null ? `${fmtPct(financed, 0)} of budget` : null}
      />
      <KpiCell
        label="Borrower class"
        value={cls || "—"}
        caption={score != null ? `Score ${score} / 10` : null}
      />
      <KpiCell
        label="Liquidity Coverage"
        value={
          req.liquidityCovered == null
            ? "—"
            : req.liquidityCovered
              ? "Sufficient Liquidity"
              : "Short"
        }
        tone={
          req.liquidityCovered == null
            ? null
            : req.liquidityCovered
              ? "good"
              : "bad"
        }
        caption={
          req.requiredLiquidity != null
            ? `Needs ${fmtMoney(req.requiredLiquidity)}${
                req.liquidityCovered === false && req.liquidityShortfall != null
                  ? ` · short ${fmtMoney(req.liquidityShortfall)}`
                  : ""
              }`
            : null
        }
      />
    </KpiStrip>
  );
}

// The "Explain this result" way in sits on top of the panels.
export const RtlPanels = ({ result, onExplain, ...actions }) => (
  <Flx column g={1.75}>
    {onExplain && <ExplainCard onExplain={onExplain} />}
    <RtlResultsColumn
      result={result}
      masonry
      hideHero
      onExplain={onExplain}
      {...actions}
    />
  </Flx>
);
